import { createTheme, Text, Title, Button, ActionIcon, rem } from '@mantine/core';

export const theme = createTheme({
  // Tipografía
  fontFamily: 'Inter, system-ui, sans-serif',
  fontFamilyMonospace: 'ui-monospace, SFMono-Regular, Monaco, Consolas, Liberation Mono, Courier New, monospace',
  
  headings: {
    fontFamily: 'Inter, system-ui, sans-serif',
    sizes: {
      h1: { 
        fontSize: rem(32), 
        fontWeight: '600', 
        lineHeight: '1.25' 
      },
      h2: { 
        fontSize: rem(24), 
        fontWeight: '600', 
        lineHeight: '1.3' },
      h3: { 
        fontSize: rem(20), 
        fontWeight: '600', 
        lineHeight: '1.35' },
    },
  },

  fontSizes: {
    xs: rem(12),         
    sm: rem(14),        
    md: rem(16),         
    lg: rem(18),         
    xl: rem(20),         
  },

  lineHeights: {
    xs: '1.4',
    sm: '1.45',
    md: '1.55',
    lg: '1.6', 
    xl: '1.65',
  },

  // 10 tonos obligatorios. Orden: claro → oscuro.
  colors: {
    brand: [
      '#eff6f9', // 0 Primario/themeLighterAlt
      '#c2dbe7', // 1 Primario/themeLighter
      '#93bed3', // 2 Primario/themeLight
      '#4385a6', // 3 Primario/themeTertiary
      '#0f587d', // 4 Primario/themeSecondary
      '#00476B', // 5 Primario/themePrimary
      '#004060', // 6 Primario/themeDarkAlt
      '#003651', // 7 Primario/themeDark
      '#00283c', // 8 Primario/themeDarker
      '#00283c', // 9 extra (repito el más oscuro para completar 10)
    ],
    // Neutros de referencia (opcionales, para usar por nombre):
    neutral: [
      '#ffffff', // 0 blanco
      '#faf9f8', // 1 Fondo/neutralLighterAlt
      '#f3f2f1', // 2 Fondo/neutralLighter
      '#edebe9', // 3 Fondo/neutralLight
      '#e1dfdd', // 4 Fondo/neutralQuaternaryAlt
      '#c8c6c4', // 5 Fondo/neutralTertiaryAlt
      '#a19f9d', // 6 Texto/neutralTertiary
      '#8a8886', // 7 Texto/neutralSecondaryAlt
      '#605e5c', // 8 Texto/neutralSecondary
      '#3b3a39', // 9 Texto/neutralPrimaryAlt
    ],
    // Nuevas paletas de colores personalizadas
    red: [
      '#fef2f2', // 0 rojo más claro
      '#fecaca', // 1 rojo claro
      '#fca5a5', // 2 rojo
      '#f87171', // 3 rojo medio claro
      '#ef4444', // 4 rojo medio
      '#dc2626', // 5 rojo primario
      '#b91c1c', // 6 rojo oscuro
      '#991b1b', // 7 rojo más oscuro
      '#7f1d1d', // 8 rojo muy oscuro
      '#7f1d1d', // 9 extra
    ],
    blue: [
      '#eff6ff', // 0 azul más claro
      '#dbeafe', // 1 azul claro
      '#bfdbfe', // 2 azul
      '#93c5fd', // 3 azul medio claro
      '#60a5fa', // 4 azul medio
      '#3b82f6', // 5 azul primario
      '#2563eb', // 6 azul oscuro
      '#1d4ed8', // 7 azul más oscuro
      '#1e40af', // 8 azul muy oscuro
      '#1e40af', // 9 extra
    ],

    yellow: [
      '#fefce8', // 0 amarillo más claro
      '#fef3c7', // 1 amarillo claro
      '#fde68a', // 2 amarillo
      '#fcd34d', // 3 amarillo medio claro
      '#fbbf24', // 4 amarillo medio
      '#f59e0b', // 5 amarillo primario
      '#d97706', // 6 amarillo oscuro
      '#b45309', // 7 amarillo más oscuro
      '#92400e', // 8 amarillo muy oscuro
      '#92400e', // 9 extra
    ],

    green: [
      '#f0fdf4', // 0 verde más claro
      '#dcfce7', // 1 verde claro
      '#bbf7d0', // 2 verde
      '#86efac', // 3 verde medio claro
      '#4ade80', // 4 verde medio
      '#22c55e', // 5 verde primario
      '#16a34a', // 6 verde oscuro
      '#15803d', // 7 verde más oscuro
      '#166534', // 8 verde muy oscuro
      '#166534', // 9 extra
    ],

  },

  primaryColor: 'brand',
  primaryShade: { light: 5, dark: 6 },

  defaultRadius: 'md',

  other: {
    black: '#000000',
    fullWidthBackground: '#F0F2F5',
  },

  components: {
    Text: Text.extend({
      defaultProps: {
        c: 'var(--mantine-other-black)',
      },
    }),
    Title: Title.extend({
      defaultProps: {
        order: 1,
        c: 'var(--mantine-other-black)',
      },
    }),
    Button: Button.extend({
      vars: (theme, props: any) => ({
        root: {
          // Solo aplicar si es brand y hay colores disponibles
          '--button-hover': 
            props?.color === 'brand' && props?.variant === 'filled' && theme?.colors?.brand?.[8] 
              ? theme.colors.brand[8]
            : props?.color === 'brand' && props?.variant === 'light' && theme?.colors?.brand?.[1]
              ? theme.colors.brand[1] 
            : undefined,
        },
      }),
    }),
    ActionIcon: ActionIcon.extend({
      vars: (theme, props: any) => ({
        root: {
          // Aplicar las mismas reglas de hover que Button para consistencia visual
          '--ai-hover': 
            props?.color === 'brand' && props?.variant === 'filled' && theme?.colors?.brand?.[8] 
              ? theme.colors.brand[8]
            : props?.color === 'brand' && props?.variant === 'light' && theme?.colors?.brand?.[1]
              ? theme.colors.brand[1] 
            : undefined,
        },
      }),
    }),
  },
});
